import { useMemo, useState } from "react";

import { Building, Check, Plus, Search, User, UserCheck } from "lucide-react";

import { useLandlords } from "hooks/useLandlords";

import { Landlord } from "js/components/types";
import { Badge } from "js/components/ui/badge";
import { Button } from "js/components/ui/button";
import { Input } from "js/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "js/components/ui/select";

import { SelectedLandlord } from "../types";
import { CreateLandlordForm } from "./CreateLandlordForm";

interface LandlordSelectorProps {
  selectedLandlord: SelectedLandlord;
  onSelectLandlord: (landlord: SelectedLandlord) => void;
  onLandlordCreated?: (landlord: Landlord) => void;
}

export const LandlordSelector = ({
  selectedLandlord,
  onSelectLandlord,
  onLandlordCreated,
}: LandlordSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showList, setShowList] = useState(true);
  const [legalTypeFilter, setLegalTypeFilter] = useState<"all" | 1 | 2>("all");

  // Fetch landlords using TanStack Query
  const {
    data: landlords = [],
    isLoading,
    isError,
    error,
  } = useLandlords({
    current: 1,
    pageSize: 100,
  });

  // Memoized filtered landlords based on search term and legal type
  const filteredLandlords = useMemo(() => {
    let filtered = landlords;

    // Filter by legal type
    if (legalTypeFilter !== "all") {
      filtered = filtered.filter(
        (landlord) => landlord.legal_type === legalTypeFilter,
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((landlord) => {
        const fullName =
          `${landlord.name} ${landlord.first_surname} ${landlord.second_surname || ""}`.toLowerCase();
        const entityName = landlord.legal_entity_name?.toLowerCase() || "";
        const email = landlord.email?.toLowerCase() || "";
        const search = searchTerm.toLowerCase();

        return (
          fullName.includes(search) ||
          entityName.includes(search) ||
          email.includes(search)
        );
      });
    }

    return filtered;
  }, [searchTerm, landlords, legalTypeFilter]);

  const getDisplayName = (landlord: Landlord | SelectedLandlord) => {
    if (landlord.legal_entity_name) {
      return landlord.legal_entity_name;
    }
    return `${landlord.name || ""} ${landlord.first_surname || ""} ${landlord.second_surname || ""}`.trim();
  };

  const getLegalTypeDisplay = (legalType: number) => {
    return legalType === 1 ? "Física" : "Moral";
  };

  const isSelected = (landlord: Landlord) => {
    return selectedLandlord?.id === landlord.id;
  };

  const handleLandlordCreated = (landlord: Landlord) => {
    onSelectLandlord(landlord);
    setShowList(false);
    if (onLandlordCreated) {
      onLandlordCreated(landlord);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-gray-500">Cargando propietarios...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-red-500">
          Error al cargar propietarios: {error?.message || "Error desconocido"}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h4 className="text-base font-semibold text-gray-900">
        Seleccionar Propietario
      </h4>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <User className="w-5 h-5 text-gray-400" />
            <p className="text-sm text-gray-600">
              Selecciona un propietario existente o crea uno nuevo
            </p>
          </div>
          <CreateLandlordForm onLandlordCreated={handleLandlordCreated}>
            <Button variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Crear Nuevo
            </Button>
          </CreateLandlordForm>
        </div>
      </div>

      <div className="space-y-4">
        {/* Search and Filters */}
        {showList &&
          ((selectedLandlord && Object.keys(selectedLandlord).length === 0) ||
            !selectedLandlord) && (
            <div className="flex gap-3">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por nombre o correo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                />
              </div>

              {/* Legal Type Filter */}
              <Select
                value={
                  legalTypeFilter === "all" ? "" : legalTypeFilter.toString()
                }
                onValueChange={(value) =>
                  setLegalTypeFilter(
                    value === "" || value === "clear"
                      ? "all"
                      : (parseInt(value) as 1 | 2),
                  )
                }
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Tipo de persona" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clear">Todos los tipos</SelectItem>
                  <SelectItem value="1">Persona Física</SelectItem>
                  <SelectItem value="2">Persona Moral</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

        {/* Selected Landlord Display */}
        {selectedLandlord && Object.keys(selectedLandlord).length > 0 && (
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  {selectedLandlord.legal_type === 1 ? (
                    <UserCheck className="w-5 h-5 text-gray-600" />
                  ) : (
                    <Building className="w-5 h-5 text-gray-600" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">
                    Propietario Seleccionado
                  </p>
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {getDisplayName(selectedLandlord)}
                    </h4>
                    <Badge
                      variant="secondary"
                      className={`text-xs ${
                        selectedLandlord.legal_type === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {getLegalTypeDisplay(selectedLandlord.legal_type ?? 1)}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    {selectedLandlord.email && (
                      <p className="text-xs text-gray-500">
                        {selectedLandlord.email}
                      </p>
                    )}
                    {selectedLandlord.phone_number && (
                      <p className="text-xs text-gray-500">
                        {selectedLandlord.phone_number}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onSelectLandlord({});
                  setShowList(true);
                }}
                className="text-gray-600 hover:text-gray-700 border-gray-300"
              >
                Cambiar
              </Button>
            </div>
          </div>
        )}

        {/* Landlords List */}
        {showList &&
          ((selectedLandlord && Object.keys(selectedLandlord).length === 0) ||
            !selectedLandlord) && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {filteredLandlords.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <User className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">
                    {searchTerm
                      ? "No se encontraron propietarios"
                      : "No hay propietarios registrados"}
                  </p>
                </div>
              ) : (
                filteredLandlords.map((landlord) => (
                  <div
                    key={landlord.id}
                    onClick={() => {
                      onSelectLandlord(landlord);
                      setShowList(false);
                    }}
                    className={`border rounded-lg p-4 py-3 cursor-pointer transition-all hover:shadow-sm ${
                      isSelected(landlord)
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex  space-x-3 flex-1 items-center">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-[5px]">
                            <h4 className="text-sm font-semibold text-gray-900">
                              {getDisplayName(landlord)}
                            </h4>
                          </div>

                          <div className="space-y-1">
                            {landlord.email && (
                              <p className="text-xs text-gray-600">
                                {landlord.email}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge
                          variant="secondary"
                          className={`text-xs ${
                            landlord.legal_type === 1
                              ? "bg-green-100 text-green-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {getLegalTypeDisplay(landlord.legal_type)}
                        </Badge>
                      </div>

                      {isSelected(landlord) && (
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
      </div>
    </div>
  );
};
